import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Database, Download, Upload, Trash2, ShieldAlert, RefreshCw, Server, HardDrive,
  Mail, Send, BellRing, Save, FileDown, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/Common";
import { BankAccountsSection } from "@/components/BankAccountsSection";
import { LicenceBudgetSection } from "@/components/LicenceBudget";
import { PayrollSettingsSection } from "@/components/PayrollSettings";
import { PublicHolidaysSection } from "@/components/PublicHolidaysSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { BackupSetupPanel } from "@/components/BackupSetupPanel";

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["scheduled-backups"],
    queryFn: () => api.listScheduledBackups(),
  });

  const createMut = useMutation({
    mutationFn: () => api.createBackup(),
    onSuccess: (res) => {
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const filename = m?.[1] || `kinos-backup-${Date.now()}.tar.gz`;
      downloadBlob(res.data, filename);
      toast.success(`Backup downloaded (${filename})`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backup failed"),
  });

  const downloadMut = useMutation({
    mutationFn: (filename) => api.downloadScheduledBackup(filename),
    onSuccess: (res, filename) => {
      downloadBlob(res.data, filename);
      toast.success(`Downloaded ${filename}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Download failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (filename) => api.deleteScheduledBackup(filename),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scheduled-backups"] });
      toast.success("Backup removed");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  // ---- Restore ----
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const restoreMut = useMutation({
    mutationFn: (f) => api.restoreBackup(f),
    onSuccess: (data2) => {
      toast.success(
        `Restore complete — restored '${data2.restored_from}' into '${data2.target_db}'.`,
        { duration: 8000 },
      );
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setConfirmOpen(false);
    },
    onError: (e) => {
      const d = e?.response?.data?.detail || "Restore failed";
      toast.error(d, { duration: 8000 });
    },
  });

  const files = data?.files || [];

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-5xl">
      <PageHeader
        overline="System"
        title="Settings"
        subtitle="Backup & restore the KINOS database. Admin-only actions are marked with a shield."
      />

      {/* Manual backup */}
      <section
        data-testid="manual-backup-section"
        className="border border-border rounded-md bg-white p-5"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-primary/5 text-primary">
            <Database className="size-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-lg">Create a backup now</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Runs <code className="mono">mongodump</code> and downloads a
              compressed <code className="mono">.tar.gz</code> of the entire
              database (all clients, invoices, quotations, POs, attendance,
              activity logs).
            </p>
          </div>
          <Button
            data-testid="create-backup-btn"
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending}
            className="bg-primary hover:bg-primary/90"
          >
            <Download className="size-4 mr-1.5" />
            {createMut.isPending ? "Creating…" : "Create backup now"}
          </Button>
        </div>
      </section>

      {/* Restore */}
      <section
        data-testid="restore-section"
        className="border border-border rounded-md bg-white p-5"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-md bg-red-50 text-red-700">
            <ShieldAlert className="size-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-lg flex items-center gap-2">
              Restore from backup
              <span className="text-[10px] font-mono uppercase tracking-wider bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                Admin only
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload a <code className="mono">.tar.gz</code> produced by this
              app. Existing collections will be <strong>dropped</strong> and
              replaced. There is no undo.
            </p>
            {!isAdmin && (
              <p className="text-xs text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-md px-2 py-1.5 mt-3 inline-block">
                Only administrators can restore backups.
              </p>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label className="overline">Backup file (.tar.gz)</Label>
              <Input
                data-testid="restore-file-input"
                ref={fileRef}
                type="file"
                accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <Button
              data-testid="restore-btn"
              onClick={() => setConfirmOpen(true)}
              disabled={!file || restoreMut.isPending}
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-50 hover:text-red-800"
            >
              <Upload className="size-4 mr-1.5" />
              {restoreMut.isPending ? "Restoring…" : "Restore"}
            </Button>
          </div>
        )}
      </section>

      {/* Enable server backups — Ubuntu setup helper */}
      <BackupSetupPanel />

      {/* Scheduled backups on disk */}
      <section
        data-testid="scheduled-backups-section"
        className="border border-border rounded-md bg-white"
      >
        <div className="flex items-start gap-3 p-5 border-b border-border">
          <div className="p-2 rounded-md bg-emerald-50 text-emerald-700">
            <Server className="size-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-lg">Server backups</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Files written by the scheduled cron job. Retention:{" "}
              <span className="mono">
                keep last {data?.keep_daily ?? 7} daily + {data?.keep_weekly ?? 4} weekly
              </span>. Directory:{" "}
              <code className="mono text-xs">{data?.directory || "/var/backups/kinos"}</code>.
            </p>
          </div>
          <Button
            data-testid="scheduled-refresh-btn"
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`size-4 mr-1.5 ${isLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {files.length === 0 ? (
          <div
            data-testid="scheduled-empty"
            className="p-10 flex flex-col items-center gap-2 text-center text-sm text-muted-foreground"
          >
            <HardDrive className="size-8 text-muted-foreground/50" />
            <p>
              No scheduled backups on disk yet. Expand{" "}
              <span className="font-semibold">
                &quot;How to enable server backups&quot;
              </span>{" "}
              above for a copy-paste setup guide.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Filename</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map((f) => (
                <TableRow key={f.filename} data-testid={`backup-row-${f.filename}`}>
                  <TableCell className="mono text-xs">{f.filename}</TableCell>
                  <TableCell className="text-xs">{fmtDate(f.created_at)}</TableCell>
                  <TableCell className="text-xs mono text-right">{fmtSize(f.size_bytes)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`download-backup-${f.filename}`}
                      variant="ghost" size="sm"
                      onClick={() => downloadMut.mutate(f.filename)}
                      disabled={downloadMut.isPending}
                      title="Download"
                    >
                      <Download className="size-4" />
                    </Button>
                    {isAdmin && (
                      <Button
                        data-testid={`delete-backup-${f.filename}`}
                        variant="ghost" size="sm"
                        onClick={() => {
                          if (window.confirm(`Delete ${f.filename}? This cannot be undone.`)) {
                            deleteMut.mutate(f.filename);
                          }
                        }}
                        disabled={deleteMut.isPending}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* Bank accounts printed on documents */}
      <BankAccountsSection isAdmin={isAdmin} />

      {/* Software renewal budget per quarter */}
      <LicenceBudgetSection isAdmin={isAdmin} />

      {/* Opening cash + INSS / withholding tax rates */}
      <PayrollSettingsSection isAdmin={isAdmin} />

      {/* Public holidays skipped in leave day counts */}
      <PublicHolidaysSection isAdmin={isAdmin} />

      {/* Weekly Outstanding Invoices Digest */}
      <DigestSection />

      {/* Reminder Center — days between reminders per document type */}
      <ReminderCenter isAdmin={isAdmin} />

      {/* Restore confirm modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="restore-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <ShieldAlert className="size-5" /> Restore will overwrite data
            </DialogTitle>
            <DialogDescription>
              This will <strong>drop every collection</strong> in the current
              database and replace it with the contents of <span className="mono">{file?.name}</span>.
              Users will be logged out and any changes since the backup was
              made will be lost. Type <code className="mono">RESTORE</code> to confirm.
            </DialogDescription>
          </DialogHeader>
          <ConfirmText onConfirm={() => file && restoreMut.mutate(file)} busy={restoreMut.isPending} onCancel={() => setConfirmOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DigestSection() {
  const qc = useQueryClient();
  const [to, setTo] = useState(null); // null = not yet hydrated
  const [busy, setBusy] = useState(false);
  const [stmtBusy, setStmtBusy] = useState(false);
  const { data: preview } = useQuery({
    queryKey: ["digest-preview"],
    queryFn: () => api.previewDigest(),
    refetchInterval: 60_000,
  });
  const { data: rs } = useQuery({
    queryKey: ["reminder-settings"],
    queryFn: () => api.getReminderSettings(),
  });
  const enabled = !!rs?.weekly_digest_enabled;
  const savedTo = rs?.weekly_digest_to || "";
  const toValue = to === null ? savedTo : to;
  const totals = preview?.totals_by_currency || {};
  const totalsLine = Object.entries(totals)
    .map(([c, v]) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`)
    .join(" · ") || "0.00";

  const settingsMut = useMutation({
    mutationFn: (body) => api.updateReminderSettings(body),
    onSuccess: (res) => {
      qc.setQueryData(["reminder-settings"], res);
      toast.success("Digest settings saved");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save"),
  });

  async function send() {
    setBusy(true);
    try {
      const r = await api.sendDigestNow(toValue.trim() || undefined);
      toast.success(`Digest sent to ${r.to} · ${r.invoice_count} invoices (PDF attached)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally {
      setBusy(false);
    }
  }

  async function runStatements() {
    if (!window.confirm("Email a statement PDF to EVERY client with an overdue balance now?")) return;
    setStmtBusy(true);
    try {
      const r = await api.runStatementsNow();
      toast.success(
        `Statements: ${r.sent} sent of ${r.overdue_clients} overdue clients` +
        (r.skipped ? ` · ${r.skipped} skipped (no email)` : "") +
        (r.failed ? ` · ${r.failed} failed` : ""),
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Statement run failed");
    } finally {
      setStmtBusy(false);
    }
  }

  return (
    <section
      data-testid="digest-section"
      className="border border-border rounded-md bg-white p-5"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-md bg-amber-50 text-amber-800">
          <Mail className="size-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-semibold text-lg">
            Outstanding invoices — weekly email digest
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            One-glance aged-receivables snapshot with a branded <b>PDF report
            attached</b>: totals by currency, aging buckets (0-7 / 8-30 / 31-60 /
            60+ days), top clients, and the worst-overdue invoices. Sends
            automatically every Monday morning when enabled.
          </p>
          {preview && (
            <p className="text-xs text-muted-foreground mt-2">
              Live preview — outstanding right now:{" "}
              <span className="font-bold text-primary mono" data-testid="digest-totals">
                {totalsLine}
              </span>
              {" · "}
              <span className="mono">{preview.invoice_count}</span> invoices ·
              <span className="mono ml-1">{preview.client_count}</span> clients
            </p>
          )}
        </div>
      </div>

      <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          data-testid="digest-weekly-toggle"
          className="size-4 accent-amber-600"
          checked={enabled}
          onChange={(e) => settingsMut.mutate({ weekly_digest_enabled: e.target.checked })}
        />
        Send automatically every Monday
      </label>
      <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer">
        <input
          type="checkbox"
          data-testid="statements-monthly-toggle"
          className="size-4 accent-amber-600"
          checked={!!rs?.monthly_statements_enabled}
          onChange={(e) => settingsMut.mutate({ monthly_statements_enabled: e.target.checked })}
        />
        Auto-email statements to every overdue client on the 1st of each month
      </label>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
        <div>
          <Label className="overline">Recipient (blank = info@kinos.tl)</Label>
          <Input
            data-testid="digest-to-input"
            type="email"
            value={toValue}
            onChange={(e) => setTo(e.target.value)}
            placeholder="finance@kinos.tl"
          />
        </div>
        <Button
          data-testid="digest-save-to-btn"
          variant="outline"
          disabled={settingsMut.isPending || toValue.trim() === savedTo}
          onClick={() => settingsMut.mutate({ weekly_digest_to: toValue.trim() })}
        >
          Save recipient
        </Button>
        <Button
          data-testid="digest-pdf-btn"
          variant="outline"
          onClick={() => api.openOutstandingPdf()}
        >
          <FileDown className="size-4 mr-1.5" /> Download PDF
        </Button>
        <Button
          data-testid="digest-send-btn"
          onClick={send}
          disabled={busy}
          className="bg-amber-600 hover:bg-amber-700"
        >
          <Send className="size-4 mr-1.5" />
          {busy ? "Sending…" : "Send digest now"}
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          data-testid="digest-whatsapp-btn"
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          disabled={!preview}
          onClick={() => {
            const text =
              `*KINOS — Outstanding invoices* (${preview.as_of})\n` +
              `Total outstanding: ${totalsLine}\n` +
              `Invoices: ${preview.invoice_count} · Clients: ${preview.client_count}`;
            window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
          }}
        >
          <MessageCircle className="size-4 mr-1.5" /> Share on WhatsApp
        </Button>
        <Button
          data-testid="statements-run-btn"
          variant="outline"
          disabled={stmtBusy}
          onClick={runStatements}
        >
          <Send className="size-4 mr-1.5" />
          {stmtBusy ? "Sending statements…" : "Send overdue statements now"}
        </Button>
      </div>
    </section>
  );
}

function ConfirmText({ onConfirm, busy, onCancel }) {
  const [txt, setTxt] = useState("");
  return (
    <>
      <Input
        data-testid="restore-confirm-input"
        placeholder='Type "RESTORE" to enable the button'
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        autoFocus
      />
      <DialogFooter className="gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}
                data-testid="restore-confirm-cancel">Cancel</Button>
        <Button
          data-testid="restore-confirm-submit"
          onClick={onConfirm}
          disabled={busy || txt.trim() !== "RESTORE"}
          className="bg-red-600 hover:bg-red-700"
        >
          {busy ? "Restoring…" : "Restore now"}
        </Button>
      </DialogFooter>
    </>
  );
}

function ReminderCenter({ isAdmin }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["reminder-settings"],
    queryFn: () => api.getReminderSettings(),
    enabled: isAdmin,
  });
  const [form, setForm] = useState({
    invoice_reminder_days: 15,
    quotation_reminder_days: 15,
    rfq_nudge_days: 3,
    best_bid_digest_enabled: false,
    portal_login_alerts_enabled: true,
    trusted_ips: "",
    licence_renewal_days: "30, 14, 7, 1",
  });
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    if (data && !hydrated) {
      setForm({
        invoice_reminder_days: data.invoice_reminder_days ?? 15,
        quotation_reminder_days: data.quotation_reminder_days ?? 15,
        rfq_nudge_days: data.rfq_nudge_days ?? 3,
        best_bid_digest_enabled: !!data.best_bid_digest_enabled,
        portal_login_alerts_enabled: data.portal_login_alerts_enabled !== false,
        trusted_ips: Array.isArray(data.trusted_ips) ? data.trusted_ips.join(", ") : "",
        licence_renewal_days: Array.isArray(data.licence_renewal_days)
          ? data.licence_renewal_days.join(", ")
          : "30, 14, 7, 1",
      });
      setHydrated(true);
    }
  }, [data, hydrated]);
  const saveMut = useMutation({
    mutationFn: () => {
      const body = { ...form };
      // Backend accepts a comma-separated string OR list; parse locally for
      // early feedback and to send the canonical array shape.
      const raw = String(form.licence_renewal_days || "").trim();
      const parsed = raw
        .split(",")
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      body.licence_renewal_days = parsed;
      body.trusted_ips = String(form.trusted_ips || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      return api.updateReminderSettings(body);
    },
    onSuccess: (res) => {
      qc.setQueryData(["reminder-settings"], res);
      toast.success("Reminder cadence saved");
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not save reminder settings"),
  });
  const [testKind, setTestKind] = useState(null);
  const testMut = useMutation({
    mutationFn: (kind) => api.sendReminderTest(kind),
    onSuccess: (res) =>
      toast.success(`Preview ${res.kind} reminder sent to ${res.sent_to}`),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not send preview"),
  });
  const digestSendMut = useMutation({
    mutationFn: () => api.sendBestBidDigestNow(),
    onSuccess: (res) =>
      toast.success(
        res.total_alerts > 0
          ? `Digest sent to ${res.recipients} recipient${res.recipients === 1 ? "" : "s"} · ${res.total_alerts} alert${res.total_alerts === 1 ? "" : "s"}`
          : "No pending best-bid alerts to digest yet.",
      ),
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not send digest"),
  });

  if (!isAdmin) return null;
  return (
    <section
      data-testid="reminder-center-section"
      className="border border-border rounded-md bg-white p-5"
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-md bg-amber-50 text-amber-700">
          <BellRing className="size-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-semibold text-lg">Reminder center</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Tune how often the nightly cron nudges people about open documents.
            All values are in days. Applied to the next reminder run — no restart needed.
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="overline">Invoice reminder every</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="reminder-invoice-days"
              type="number" min="1" max="365"
              value={form.invoice_reminder_days}
              onChange={(e) =>
                setForm({ ...form, invoice_reminder_days: Number(e.target.value) })
              }
              className="w-24"
              disabled={isLoading}
            />
            <span className="text-xs text-muted-foreground">days after due</span>
          </div>
        </div>
        <div>
          <Label className="overline">Quotation reminder every</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="reminder-quotation-days"
              type="number" min="1" max="365"
              value={form.quotation_reminder_days}
              onChange={(e) =>
                setForm({ ...form, quotation_reminder_days: Number(e.target.value) })
              }
              className="w-24"
              disabled={isLoading}
            />
            <span className="text-xs text-muted-foreground">days without action</span>
          </div>
        </div>
        <div>
          <Label className="overline">RFQ nudge, before validity</Label>
          <div className="flex items-center gap-2">
            <Input
              data-testid="reminder-rfq-days"
              type="number" min="1" max="365"
              value={form.rfq_nudge_days}
              onChange={(e) =>
                setForm({ ...form, rfq_nudge_days: Number(e.target.value) })
              }
              className="w-24"
              disabled={isLoading}
            />
            <span className="text-xs text-muted-foreground">days before closing</span>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Label className="overline">Software licence renewal nudges</Label>
        <div className="flex items-center gap-2">
          <Input
            data-testid="reminder-licence-days"
            value={form.licence_renewal_days}
            onChange={(e) =>
              setForm({ ...form, licence_renewal_days: e.target.value })
            }
            className="w-64 mono text-sm"
            placeholder="30, 14, 7, 1"
            disabled={isLoading || !hydrated}
          />
          <span className="text-xs text-muted-foreground">
            comma-separated days before expiry (e.g. <span className="mono">30, 14, 7, 1</span>)
          </span>
        </div>
      </div>

      <div className="mt-5 rounded-md border border-border bg-slate-50 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <label className="flex items-start gap-3 cursor-pointer max-w-xl">
            <input
              data-testid="best-bid-digest-toggle"
              type="checkbox"
              checked={!!form.best_bid_digest_enabled}
              disabled={isLoading || !hydrated}
              onChange={(e) =>
                setForm({ ...form, best_bid_digest_enabled: e.target.checked })
              }
              className="mt-1 size-4"
            />
            <span>
              <span className="block text-sm font-semibold">
                Batch best-bid alerts into a daily digest
              </span>
              <span className="block text-xs text-muted-foreground mt-0.5">
                When on, KINOS stops pinging you every time a portal bid beats
                the current best price. Instead, one consolidated email arrives
                the next time the reminder cron runs. Great for owners of
                high-volume RFQs.
              </span>
            </span>
          </label>
          <Button
            data-testid="best-bid-digest-send-now"
            variant="outline"
            size="sm"
            disabled={digestSendMut.isPending}
            onClick={() => digestSendMut.mutate()}
          >
            <Mail className="size-3.5 mr-1.5" />
            {digestSendMut.isPending ? "Sending…" : "Send pending digest now"}
          </Button>
        </div>
      </div>

      <div className="mt-3 rounded-md border border-border bg-slate-50 p-4">
        <label className="flex items-start gap-3 cursor-pointer max-w-xl">
          <input
            data-testid="portal-login-alerts-toggle"
            type="checkbox"
            checked={!!form.portal_login_alerts_enabled}
            disabled={isLoading || !hydrated}
            onChange={(e) =>
              setForm({ ...form, portal_login_alerts_enabled: e.target.checked })
            }
            className="mt-1 size-4"
          />
          <span>
            <span className="block text-sm font-semibold">
              Email admins when a client signs into their portal
            </span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              A quick &quot;Portal login — client&quot; heads-up so you know statements
              were seen. Untick to mute these emails when they get noisy; sign-ins
              are still recorded in the activity log either way.
            </span>
          </span>
        </label>
        <div className="mt-3 pl-7">
          <Label className="overline">Trusted office IPs (never alert)</Label>
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              data-testid="trusted-ips-input"
              value={form.trusted_ips}
              onChange={(e) => setForm({ ...form, trusted_ips: e.target.value })}
              className="w-96 mono text-sm"
              placeholder="203.0.113.5, 10.0.0.0/24"
              disabled={isLoading || !hydrated}
            />
            <span className="text-xs text-muted-foreground">
              comma-separated IPs or CIDR ranges — sign-ins from these never trigger
              &quot;new IP&quot; alerts
            </span>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {[
            { k: "invoice", label: "Invoice preview" },
            { k: "quotation", label: "Quotation preview" },
            { k: "rfq", label: "RFQ preview" },
          ].map(({ k, label }) => (
            <Button
              key={k}
              data-testid={`reminder-test-${k}`}
              variant="outline"
              size="sm"
              disabled={testMut.isPending && testKind === k}
              onClick={() => {
                setTestKind(k);
                testMut.mutate(k);
              }}
            >
              <Mail className="size-3.5 mr-1.5" />
              {testMut.isPending && testKind === k ? "Sending…" : `Send me a ${label}`}
            </Button>
          ))}
        </div>
        <Button
          data-testid="reminder-save-btn"
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending || isLoading || !hydrated}
          className="bg-primary hover:bg-primary/90"
        >
          <Save className="size-4 mr-1.5" />
          {saveMut.isPending ? "Saving…" : "Save reminder cadence"}
        </Button>
      </div>
    </section>
  );
}
